<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Popover from 'dashboard/components-next/popover/Popover.vue';
import ContentTemplateForm from './ContentTemplateForm.vue';

const props = defineProps({
  inboxId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['sendMessage']);

const { t } = useI18n();
const store = useStore();
const inbox = useMapGetter('inboxes/getInbox');
const currentUser = useMapGetter('getCurrentUser');

const searchQuery = ref('');
const selectedTemplate = ref(null);

const contentTemplates = computed(() => {
  const inboxData = inbox.value(props.inboxId);
  return inboxData?.content_templates?.templates || [];
});

// Alias de visualización por bandeja (definidos en Ajustes > Plantillas):
// la plantilla se muestra Y SE BUSCA también por su nombre personalizado.
const fetchInboxAliases = () => {
  if (!props.inboxId) return;
  const loaded = store.getters['templateClassifications/isInboxLoaded'](
    props.inboxId
  );
  if (!loaded) {
    // eslint-disable-next-line no-console
    console.log(
      '[ContentTemplateSelector] fetching aliases for inbox',
      props.inboxId
    );
    store.dispatch('templateClassifications/get', { inboxId: props.inboxId });
  }
};
onMounted(fetchInboxAliases);
watch(() => props.inboxId, fetchInboxAliases);

const templateDisplayNames = computed(() => {
  const hydrated = store.getters['templateClassifications/getTemplates'](
    props.inboxId
  );
  const fromInbox = {};
  (hydrated || []).forEach(tpl => {
    if (tpl.display_name) fromInbox[tpl.name] = tpl.display_name;
  });
  return Object.keys(fromInbox).length
    ? fromInbox
    : currentUser.value?.template_display_names || {};
});

const displayNameOf = template =>
  templateDisplayNames.value[template.friendly_name] || template.friendly_name;

const filteredTemplates = computed(() => {
  const q = searchQuery.value.toLowerCase();
  return contentTemplates.value.filter(
    template =>
      template.status === 'approved' &&
      (template.friendly_name.toLowerCase().includes(q) ||
        displayNameOf(template).toLowerCase().includes(q))
  );
});

const handlePopoverShow = () => {
  searchQuery.value = '';
  selectedTemplate.value = null;
};

const handlePopoverHide = () => {
  selectedTemplate.value = null;
};

const handleTemplateClick = template => {
  selectedTemplate.value = template;
};

const handleBack = () => {
  selectedTemplate.value = null;
};

const handleSendMessage = (template, hide) => {
  emit('sendMessage', template);
  hide();
};
</script>

<template>
  <Popover
    align="start"
    disable-mobile-view
    @show="handlePopoverShow"
    @hide="handlePopoverHide"
  >
    <Button
      icon="i-ph-whatsapp-logo"
      :label="t('COMPOSE_NEW_CONVERSATION.FORM.TWILIO_OPTIONS.LABEL')"
      color="slate"
      size="sm"
      :disabled="selectedTemplate"
      class="!text-xs font-medium"
    />
    <template #content="{ hide }">
      <div
        v-if="!selectedTemplate"
        class="flex flex-col gap-2 p-4 items-center w-[21.875rem]"
      >
        <div class="w-full">
          <Input
            v-model="searchQuery"
            type="search"
            :placeholder="
              t(
                'COMPOSE_NEW_CONVERSATION.FORM.TWILIO_OPTIONS.SEARCH_PLACEHOLDER'
              )
            "
            custom-input-class="ltr:pl-10 rtl:pr-10"
          >
            <template #prefix>
              <Icon
                icon="i-lucide-search"
                class="absolute top-2 size-3.5 ltr:left-3 rtl:right-3"
              />
            </template>
          </Input>
        </div>
        <div
          v-for="template in filteredTemplates"
          :key="template.content_sid"
          tabindex="0"
          class="flex flex-col gap-2 p-2 w-full rounded-lg cursor-pointer dark:hover:bg-n-alpha-3 hover:bg-n-alpha-1"
          @click="handleTemplateClick(template)"
        >
          <div class="flex justify-between items-center">
            <span class="text-sm text-n-slate-12">
              {{ displayNameOf(template) }}
              <span
                v-if="displayNameOf(template) !== template.friendly_name"
                class="ms-1 text-[10px] text-n-slate-9"
              >
                ({{ template.friendly_name }})
              </span>
            </span>
          </div>
          <p class="mb-0 text-xs leading-5 text-n-slate-11 line-clamp-2">
            {{ template.body || t('CONTENT_TEMPLATES.PICKER.NO_CONTENT') }}
          </p>
        </div>
        <template v-if="filteredTemplates.length === 0">
          <p class="pt-2 w-full text-sm text-n-slate-11">
            {{ t('COMPOSE_NEW_CONVERSATION.FORM.TWILIO_OPTIONS.EMPTY_STATE') }}
          </p>
        </template>
      </div>
      <ContentTemplateForm
        v-else
        :template="selectedTemplate"
        @send-message="payload => handleSendMessage(payload, hide)"
        @back="handleBack"
      />
    </template>
  </Popover>
</template>
